import Flex from "common/Boxes/Flex";
import GroupName from "features/newMarketplace/common/GroupName";
import { typography } from "@shared/common/Themes/typography";
import GroupRate from "features/newMarketplace/common/GroupRate";
import Text from "common/Typography/Text";
import React, { useContext } from "react";
import styled, { css, useTheme } from "styled-components";
import { Bot } from "lucide-react";
import UserOutlined from "@ant-design/icons/UserOutlined";
import ScoreTag from "features/newMarketplace/common/ScoreTag";
import useMedia from "utils/hooks/useMedia";
import { MOBILE_VIRTUAL_LIST } from "common/constants";
import { GroupsPageContext } from "@shared/features/groups/GroupsPage.context";
import { SignalBotsIcon } from "common/Icons/Icons";
import CreateBotButton from "features/newMarketplace/common/CreateBotButton";
import { GroupMainInfoProps } from "@shared/features/newMarketplace/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";
import { selectWorkspace } from "@shared/slices/common.slice.ts";
import { useSelector } from "react-redux";
import { WorkspaceType } from "@shared/types";
import { phoneTwoLineTitle } from "common/NewLook/layout.ts";

const GroupMainInfo = ({
  isOnboarding,
  image,
  name,
  stars,
  numReviewers,
  numSubscribers,
  scoreTagProps,
  groupId,
  hideCreateButton,
  groupData,
  isMyGroups,
}: GroupMainInfoProps) => {
  const workspace = useSelector(selectWorkspace);
  const theme = useTheme();
  const { isGroupsPage, groupIdToNumBots } =
    useContext(GroupsPageContext) || {};
  const isMobile = useMedia(
    [`(max-width: ${MOBILE_VIRTUAL_LIST})`],
    [true],
    false
  );
  const { t: tGroups } = useTypedTranslation(LanguageNameSpaces.GROUPS);
  const { t: tMarketplace } = useTypedTranslation(
    LanguageNameSpaces.MARKETPLACE
  );
  const isAdminWorkspace = workspace === WorkspaceType.ADMIN;
  // New look: a lighter identity block on the My Groups page.
  const isNewLookGroups = theme.isNewLook && !!isGroupsPage;

  const getGroupRatingDisplay = () => {
    return (
      <>
        <GroupRate
          groupAvgStars={stars ?? 0}
          groupNumReviewers={numReviewers ?? 0}
          starsSize={13}
          isShort={true}
        />
        {isOnboarding && (
          <Flex $justifyContent="flex-start" style={{ paddingTop: 2 }}>
            <StyledUserOutlined
              style={{
                fontSize: isMobile
                  ? typography.fontSizes.S
                  : typography.fontSizes.M,
                top: -2,
                position: "relative",
              }}
            />
            <Text
              type="secondary"
              $size={isMobile ? typography.fontSizes.S : typography.fontSizes.M}
            >
              {tMarketplace("card.subscribers", { count: numSubscribers })}
            </Text>
          </Flex>
        )}
      </>
    );
  };

  const getTitleSubtext = () => {
    if (isAdminWorkspace) return;
    if (isGroupsPage && isMyGroups) {
      return (
        <Flex $justifyContent="flex-start">
          {isNewLookGroups ? (
            <Bot
              size={14}
              strokeWidth={1.75}
              color={theme.colors.secondaryText}
              style={{ marginRight: 6, flex: "none" }}
            />
          ) : (
            <SignalBotsIcon
              fillColor={theme.colors.secondaryGrey}
              style={{ marginRight: 8 }}
            />
          )}
          <Text type="secondary" $size={isNewLookGroups ? "12.5px" : undefined}>
            {tGroups("my_group.active_signals_bots", {
              num: (!!groupId && groupIdToNumBots?.[groupId]) || "0",
            })}
          </Text>
        </Flex>
      );
    } else return getGroupRatingDisplay();
  };

  const scoreTag = <ScoreTag {...scoreTagProps} />;

  return (
    <Flex style={{ position: "relative", width: "100%" }}>
      <Flex
        $justifyContent="flex-start"
        style={{ overflow: "hidden", flex: 1 }}
      >
        {image ? (
          <Logo
            src={image}
            alt=""
            $isOnboarding={isOnboarding}
            $isMobile={isMobile}
            $newLook={isNewLookGroups}
          />
        ) : (
          <Logo
            as="div"
            aria-hidden="true"
            $isOnboarding={isOnboarding}
            $isMobile={isMobile}
            $newLook={isNewLookGroups}
          >
            {name?.slice(0, 1).toUpperCase()}
          </Logo>
        )}
        <NameBlock
          $wraps={isNewLookGroups}
          style={{
            overflow: "hidden",
            flex: 1,
            paddingRight: 8,
            marginLeft: isGroupsPage && scoreTagProps?.score ? 10 : 0,
          }}
        >
          <GroupName
            name={name}
            level={isOnboarding && isMobile ? 4 : 3}
            style={
              isNewLookGroups
                ? NEW_LOOK_NAME_STYLE
                : { fontWeight: typography.fontWeights.SEMI_BOLD }
            }
          />
          {getTitleSubtext()}
        </NameBlock>
      </Flex>
      {!isGroupsPage && scoreTag}
      {isGroupsPage && (
        <StoreTagWrapper $isMobile={isMobile}>{scoreTag}</StoreTagWrapper>
      )}
      {!hideCreateButton && groupData && isGroupsPage && !isAdminWorkspace && (
        <CreateBotButton groupData={groupData} />
      )}
    </Flex>
  );
};

export default GroupMainInfo;

const NEW_LOOK_NAME_STYLE = {
  fontSize: 15,
  fontWeight: typography.fontWeights.SEMI_BOLD,
  marginBottom: 2,
};

const NameBlock = styled.div<{ $wraps: boolean }>`
  ${(p) =>
    p.$wraps &&
    css`
      h3,
      h4 {
        ${phoneTwoLineTitle};
      }
    `}
`;
const Logo = styled.img<{
  $isOnboarding?: boolean;
  $isMobile?: boolean;
  $newLook?: boolean;
}>`
  display: grid;
  place-items: center;
  flex-shrink: 0;
  font-size: 24px;
  background: ${(p) => p.theme.colors.lightBlue};
  color: ${(p) => p.theme.colors.primaryBlue};
  height: ${(p) => (p.$isOnboarding && !p.$isMobile ? 60 : 46)}px;
  width: ${(p) => (p.$isOnboarding && !p.$isMobile ? 60 : 46)}px;
  border-radius: 8px;
  margin-right: 16px;
  ${(p) =>
    p.$newLook &&
    css`
      width: 44px;
      height: 44px;
      border-radius: 8px;
      margin-right: 12px;
      font-size: 18px;
      font-weight: ${p.theme.typography.fontWeights.EXTRA_BOLD};
    `}
`;
const StyledUserOutlined = styled(UserOutlined)`
  margin-right: 6px;
  color: ${(p) => p.theme.colors.secondaryGrey};
`;
const StoreTagWrapper = styled.div<{ $isMobile: boolean }>`
  position: absolute;
  bottom: -5px;
  left: ${(p) => (p.$isMobile ? 12 : 24)}px;
`;
